import * as XLSX from 'xlsx';

/** Minimum digits for a plausible phone number (E.164 validation happens on the server). */
export function isPlausiblePhone(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 7 && /[\d+]/.test(raw);
}

export function normalizePhoneToken(token) {
  return String(token || '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/[,;]/g, '');
}

export function parsePhonesFromText(text) {
  const raw = String(text || '')
    .split(/[\n,;]+/)
    .map(normalizePhoneToken)
    .filter(isPlausiblePhone);
  return [...new Set(raw)];
}

export function parsePhonesFromSpreadsheetRows(rows) {
  const raw = [];
  for (const row of rows) {
    if (!row || !row.length) continue;
    const cell = row[0];
    if (cell == null) continue;
    const token = normalizePhoneToken(String(cell));
    if (isPlausiblePhone(token)) raw.push(token);
  }
  return [...new Set(raw)];
}

export function parsePhonesFromFile(file, data) {
  try {
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      const lines = String(data).split(/\r?\n/);
      const tokens = lines.flatMap((line) =>
        line.split(/[,;]/).map(normalizePhoneToken),
      );
      return [...new Set(tokens.filter(isPlausiblePhone))];
    }

    const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const imported = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    return parsePhonesFromSpreadsheetRows(imported);
  } catch (error) {
    console.error('Error parsing phone file:', error);
    return [];
  }
}

export function readImportFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => resolve(evt.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

export function formatPhonesForImport(contacts) {
  return contacts
    .map((contact) => {
      if (typeof contact === 'string') return contact.trim();
      if (typeof contact === 'object' && contact?.phone != null) {
        return String(contact.phone).trim();
      }
      return '';
    })
    .filter((phone) => phone !== '' && isPlausiblePhone(phone));
}
